import React, { useState, useEffect } from "react";
import { Input, Button, Table, Form, Space, message, Row, Col, Popover, Spin, Select, Skeleton, Tooltip } from "antd";
import { CloseOutlined } from '@ant-design/icons';
import * as com from "../../Common"
import "./OpenHouse.css"
import GeoLocationInput from "../../Common/components/GeoLocationInput"
import { useHistory } from "react-router-dom";
import { track } from "statsig";
import { STATSIG, PAGE } from "statsig/events";

interface OpenHouse {
  key: number;
  address: string;
  city: string;
  state: string;
  zip: string;
  price: number;
  daysonmarket: number;
  agentname: string;
  agentphone: string;
  agentemail: string;
  distance: number;
  county?: string;
  hasIncomeData?: boolean;
  mfi?: number;
  ami?: number;
}

interface ExpandAddress {
  address: string;
  city: string;
  state: string;
  county: string;
  zip: string;
}

interface Location {
  lat: number;
  lng: number;
  address: string;
}

interface PaginationConfig {
  current: number;
  pageSize: number;
  showSizeChanger: boolean;
  position: ('topLeft' | 'topCenter' | 'topRight' | 'bottomLeft' | 'bottomCenter' | 'bottomRight')[];
}

interface TableColumnType {
  title: string;
  dataIndex: string;
  key: string;
  render?: (text: any, record: OpenHouse) => React.ReactNode;
  sorter?: (a: OpenHouse, b: OpenHouse) => number;
  defaultSortOrder?: 'ascend' | 'descend';
  width?: number;
}

interface ListingResponse {
    FullAddress: string;
    City: string;
    County: string;
    State: string;
    ZipCode: string;
    Price: string;
    DaysOnMarket: number;
    AgentName: string;
    AgentPhone: string;
    AgentEmail: string;
    Distance: number;
}

const OpenHouseFinder: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [incomeInfoLoading, setIncomeInfoLoading] = useState<boolean>(false);
    const [incomeLoadingError, setIncomeLoadingError] = useState<boolean>(false);
    const [openHouseData, setOpenHouseData] = useState<OpenHouse[]>([]);
    const [originalData, setOriginalData] = useState<OpenHouse[]>([]);
    const [listingUnavailable, setListingUnavailable] = useState<boolean>(false);
    const [openedPopoverId, setOpenedPopoverId] = useState<string | null>(null);
    const [mfi, setMFI] = useState<number>(0);
    const [ami, setAMI] = useState<number>(0);
    const [currentFilter, setCurrentFilter] = useState<string>("");
    const [location, setLocation] = useState<Location | null>(null);
    const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState<string | null>(null);
    const [phoneCopySuccess, setPhoneCopySuccess] = useState<string | null>(null);
    const [showDefault, setShowDefault] = useState<boolean>(true);
    const [emailCopySuccess, setEmailCopySuccess] = useState<string | null>(null);
    const [currentPageData, setCurrentPageData] = useState<OpenHouse[]>([]);
    const [GeoInputError, setGeoInputError] = useState<string>("");
    const [expandAddress, setExpandAddress] = useState<ExpandAddress>({
        address: "",
        city: "",
        state: "",
        county: "",
        zip: "",
    });
    const [pagination, setPagination] = useState<PaginationConfig>({
        current: 1,
        pageSize: 20,
        showSizeChanger: false,
        position: ["bottomLeft"],
    });

    const viewPortWidth = window.innerWidth;
    const history = useHistory();

    useEffect(() => {
        track(STATSIG.COMMON.VIEW, PAGE.OPEN_HOUSE_FINDER, { page: PAGE.OPEN_HOUSE_FINDER });
    }, []);

    // useEffect(() => {
    //     setIncomeInfoLoading(true);
    //     getMFI(expandAddress.address, expandAddress.city, expandAddress.state, expandAddress.zip);
    //     getAMI(expandAddress.state, expandAddress.county);
    // }, [expandAddress]);

    useEffect(() => {
        handleFilterUpdate(currentFilter);
    }, [currentFilter]);

    useEffect(() => {
        if (currentPageData.length > 0) {
            setIncomeInfoLoading(true);
            fetchIncomeDataForCurrentPage();
        }
    }, [currentPageData, pagination.current]);

    const fetchIncomeDataForCurrentPage = async (): Promise<void> => {
        const updatedData = [...openHouseData];
    
        // Create an array of promises for all records
        const fetchPromises = currentPageData.map(async (record) => {
            try {
                // Run both fetch requests in parallel for each record
                const [mfiResponse, amiResponse] = await Promise.all([
                    fetch("/data/loanlimitswithincome", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            Address: record.address.split(",")[0],
                            City: record.city,
                            State: com.mapAbbrFullName[record.state],
                            Zip: record.zip,
                        })
                    }),
                    fetch("/data/gethudmedianfamilyincome", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            State: com.mapAbbrFullName[record.state],
                            County: record.county + " County",
                        })
                    })
                ]);
    
                if (!mfiResponse.ok || !amiResponse.ok) {
                    throw new Error("Failed to fetch income data");
                }
    
                const [mfiData, amiData] = await Promise.all([
                    mfiResponse.json(),
                    amiResponse.json()
                ]);
    
                return {
                    address: record.address,
                    data: {
                        hasIncomeData: true,
                        mfi: mfiData.MedianIncome,
                        ami: amiData.MedianIncome,
                    }
                };
            } catch (error) {
                console.error("Error fetching income data:", error);
                return {
                    address: record.address,
                    data: {
                        hasIncomeData: false
                    }
                };
            }
        });
    
        // Wait for all requests to complete
        const results = await Promise.all(fetchPromises);
    
        // Update the data with all results
        results.forEach(result => {
            const listingIndex = updatedData.findIndex((listing) => listing.address === result.address);
            if (listingIndex !== -1) {
                updatedData[listingIndex] = {
                    ...updatedData[listingIndex],
                    ...result.data
                };
            }
        });
    
        setIncomeInfoLoading(false);
        setOpenHouseData(updatedData);
    };


    const handleOpenHouseSearch = async (): Promise<void> => {
        if (!location) {
            setGeoInputError("Please provide a target location");
            return;
        }
        setCurrentFilter("");
        setLoading(true);
        setShowDefault(false);
        setPagination({
            ...pagination,
            current: 1,
        });
        try {
            const token = sessionStorage.getItem("ZeitroA");
            const response: Response = await fetch("/los/openhousesearch", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify({
                    Lat: location.lat,
                    Lon: location.lng,
                    Dist: parseFloat(form.getFieldValue("radius")),
                    Addr: location.address,
                })
            });
            
            if (!response.ok) {
                throw new Error("Failed to fetch listing data");
            }
            
            const jsonResponse: ListingResponse[] | null = await response.json();
            
            let data: OpenHouse[] = [];
            if (!jsonResponse) {
                setListingUnavailable(true);
            } else {
                data = jsonResponse.map((listing: ListingResponse, index: number) => ({
                    key: index,
                    address: listing.FullAddress,
                    city: listing.City,
                    county: listing.County,
                    state: listing.State,
                    zip: listing.ZipCode,
                    price: parseFloat(listing.Price.trim()),
                    daysonmarket: listing.DaysOnMarket,
                    agentname: listing.AgentName,
                    agentphone: listing.AgentPhone,
                    agentemail: listing.AgentEmail,
                    distance: listing.Distance,
                }));
                setListingUnavailable(false);
            }
            
            setOpenHouseData(data.sort((a, b) => a.daysonmarket - b.daysonmarket));
            setOriginalData(data.sort((a, b) => a.daysonmarket - b.daysonmarket));

            const startIndex = 0;
            const endIndex = pagination.pageSize;
            const initialPageData = data.slice(startIndex, endIndex);
            setCurrentPageData(initialPageData);
        } catch (error) {
            console.error("Error fetching open house data:", error);
            message.error("Failed to get listing data. Please try again later.");
        } finally {
            setLoading(false);
            
        }
    };

    const handlePopoverOpenChange = (open: boolean, record: OpenHouse): void => {
        setOpenedPopoverId(open ? record.key.toString() : null);

    };

    const handleFilterUpdate = (value: string): void => {
        let filtered: OpenHouse[] = [...originalData];
        
        if (value !== "") {
            if (value === "less500") {
                filtered = filtered.filter((listing) => listing.price <= 500000);
            } else if (value === "500to1000") {
                filtered = filtered.filter((listing) => listing.price > 500000 && listing.price <= 1000000);
            } else if (value === "1000to3000") {
                filtered = filtered.filter((listing) => listing.price > 1000000 && listing.price <= 3000000);
            } else if (value === "over3000") {
                filtered = filtered.filter((listing) => listing.price > 3000000);
            }
        }
        
        setOpenHouseData(filtered);
        setPagination({
            ...pagination,
            current: 1,
        });
        setCurrentPageData(filtered);
    };

    const handleTableChange = (newPagination: any, filters: any, sorter: any, { action }: { action: string }): void => {
        // Only proceed if the action is related to pagination
        if (action === 'paginate') {        
            const startIndex = (newPagination.current - 1) * newPagination.pageSize;
            const endIndex = startIndex + newPagination.pageSize;
            const currData = openHouseData.slice(startIndex, endIndex);
            
            setPagination({
                ...pagination,
                current: newPagination.current,
                pageSize: newPagination.pageSize,
            });

            setCurrentPageData(currData);
        }
    };

    const renderExpandIcon = (
        expanded: boolean, 
        onExpand: (record: OpenHouse, e: React.MouseEvent<HTMLElement, MouseEvent>) => void, 
        record: OpenHouse
    ): React.ReactNode => (
        <div style={{ textAlign: 'right' }}>
            <img
                src={`/images/rates-lenders-${expanded ? 'up' : 'down'}.svg`}
                style={{ cursor: "pointer" }} 
                onClick={(e) => onExpand(record, e)}
                alt={expanded ? "Collapse" : "Expand"}
            />
        </div>
    );

    const renderAddressColumn = (address: string, record: OpenHouse): React.ReactNode => {

        const handleCopy = (record: OpenHouse) => {
            navigator.clipboard.writeText(address);
            setCopySuccess(address);
            setTimeout(() => {
                setCopySuccess(null);
            }, 2000);
        }

        return (
            <div className="d-flex">
                <Tooltip 
                    overlayClassName="openhouse-address-tooltip"
                    arrow={false}
                    placement="bottomRight"
                    title={
                        <div>
                            <span className="openhouse-address-tooltip-text">{address}</span>
                        </div>
                    }
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="openhouse-address-text">{address}</span>
                    </div>
                </Tooltip>
                <img 
                    src="/images/copy-light.svg" 
                    alt="Copy" 
                    style={{ cursor: 'pointer', marginLeft: '5px' }}
                    onClick={() => handleCopy(record)}
                />
                {copySuccess === address && (
                    <div
                        className="openhouse-copy-success"
                    >
                        <img src="/images/green-check.svg" alt="Copy check" />
                        <div className="openhouse-copy-success-text">Copied!</div>
                    </div>
                )}
            </div>
        )
    };

    const formatPhoneNumber = (phoneNumber: string): string => {
        // Remove all non-digit characters
        const cleaned: string = phoneNumber.replace(/\D/g, '');
        
        // Check if we have a valid 10-digit number
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        
        // Return original if not a standard 10-digit number
        return phoneNumber;
    };

    const renderAgentInfo = (_text: string, record: OpenHouse): React.ReactNode => {

        const handlePhoneCopy = (record: OpenHouse) => {
            navigator.clipboard.writeText(formatPhoneNumber(record.agentphone));
            setPhoneCopySuccess(record.agentphone);
            setTimeout(() => {
                setPhoneCopySuccess(null);
            }, 2000);
        }

        const handleEmailCopy = (record: OpenHouse) => {
            navigator.clipboard.writeText(record.agentemail);
            setEmailCopySuccess(record.agentemail);
            setTimeout(() => {
                setEmailCopySuccess(null);
            }, 2000);
        }

        return(
            <div>
                <Popover
                    rootClassName="openhouse-popup"
                    open={openedPopoverId === record.key.toString()}
                    onOpenChange={(open: boolean) => handlePopoverOpenChange(open, record)}
                    content={
                        <div className="agent-popup">
                            <div className="agent-popup-header">
                                <b>SELLER AGENT</b>
                                <CloseOutlined 
                                    rev="undefined"
                                    style={{ cursor: 'pointer' }} 
                                    onClick={() => setOpenedPopoverId(null)}
                                />
                            </div>
                            <div className="agent-popup-content">
                                <div className="agent-popup-name">{record.agentname}</div>
                                <div className="agent-popup-contact">
                                    {record.agentphone && (
                                        <div>
                                            {formatPhoneNumber(record.agentphone)} 
                                            <img 
                                                src="/images/copy-light.svg" 
                                                alt="Copy" 
                                                style={{ cursor: 'pointer', marginLeft: '5px' }}
                                                onClick={() => handlePhoneCopy(record)}
                                            />
                                            {phoneCopySuccess === record.agentphone && (
                                                <div className="openhouse-popconfirm-copy-success">
                                                    <img src="/images/green-check.svg" alt="Copy check" />
                                                    <div className="openhouse-copy-success-text">Copied!</div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {record.agentemail && (
                                    <div className="agent-popup-contact">
                                        {record.agentemail}
                                        <img 
                                            src="/images/copy-light.svg" 
                                            alt="Copy" 
                                            style={{ cursor: 'pointer', marginLeft: '5px' }}
                                            onClick={() => handleEmailCopy(record)}
                                        />
                                        {emailCopySuccess === record.agentemail && (
                                            <div className="openhouse-popconfirm-copy-success">
                                                <img src="/images/green-check.svg" alt="Copy check" />
                                                <div className="openhouse-copy-success-text">Copied!</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    }
                    trigger="click"
                    placement="right"
                >
                    <span style={{ cursor: 'pointer', color: '#325CEB', fontWeight: '500' }}>
                        {record.agentname}
                    </span>
                </Popover>
            </div>
        )
    };

    const renderIncomeInfo = (record: OpenHouse): React.ReactNode => {
        if (incomeInfoLoading) {
            return (
                <div className="expanded-row-container d-flex justify-content-center align-items-center">
                    <Spin />
                </div>
            );
        }

        if (incomeLoadingError) {
            return (
                <div className="expanded-row-container d-flex justify-content-center align-items-center">
                    <b>Could not find income limit info for this address.</b>
                </div>
            );
        }

        return (
            <div className="expanded-row-container">
                <Row className="expanded-row-content">
                    <Col className="expanded-row-content-col">
                        <div className="mb-2">80% AMI:  &nbsp;<b>${com.commaize(record.ami * 0.8)}</b></div>
                        <div className="mb-2">80% MFI:  &nbsp;<b>${com.commaize(record.mfi * 0.8)}</b></div>
                    </Col>
                    <Col className="expanded-row-content-col">
                        <div className="mb-2">100% AMI:  &nbsp;<b>${com.commaize(record.ami)}</b></div>
                        <div className="mb-2">100% MFI:  &nbsp;<b>${com.commaize(record.mfi)}</b></div>
                    </Col>
                    <Col className="expanded-row-content-col">
                        <div className="mb-2">150% AMI:  &nbsp;<b>${com.commaize(record.ami * 1.5)}</b></div>
                        <div className="mb-2">150% MFI:  &nbsp;<b>${com.commaize(record.mfi * 1.5)}</b></div>
                    </Col>
                </Row>
            </div>
        );
    };

    const columns: TableColumnType[] = [
        {
            title: "Address",
            dataIndex: "address",
            key: "address",
            render: (address: string, record: OpenHouse) => renderAddressColumn(address, record),
            width: viewPortWidth > 1280 ? 500 : 350,
        },
        {
            title: "Days listed",
            dataIndex: "daysonmarket",
            key: "daysonmarket",
            sorter: (a: OpenHouse, b: OpenHouse) => a.daysonmarket - b.daysonmarket,
            defaultSortOrder: "ascend",
        },
        {
            title: "Listed price",
            dataIndex: "price",
            key: "price",
            render: (price: number) => `$${com.commaize(price)}`,
            sorter: (a: OpenHouse, b: OpenHouse) => a.price - b.price,
        },
        {
            title: "Distance",
            dataIndex: "distance",
            key: "distance",
            render: (distance: number) => `${distance} mi`,
            sorter: (a: OpenHouse, b: OpenHouse) => a.distance - b.distance,
        },
        {
            title: "Agent",
            dataIndex: "agentname",
            key: "agentname",
            render: renderAgentInfo,
            sorter: (a: OpenHouse, b: OpenHouse) => a.agentname.localeCompare(b.agentname),
        },
    ];

    return (
        <div className="openhouse-container">
            <div className="openhouse-header">
                <span 
                    className="openhouse-header-text openhouse-title"
                    onClick={() => history.push("/app/growthhub")}
                >
                    Growth hub
                </span>
                <img src="/images/arrow-bar.svg" />
                <span className="openhouse-title">Open house finder</span>
            </div>
            
            <Form 
                form={form} 
                onFinish={handleOpenHouseSearch}
                initialValues={{ radius: 5 }}
            >
                <Row className="openhouse-form-row">
                    <div className="openhouse-inputs-panel">
                        <GeoLocationInput 
                            onLocationSelect={(loc: Location) => setLocation(loc)}
                            width="460px"
                            height="48px"
                            placeholder="Search by city, county, or zip code..."
                            displayError={GeoInputError}
                            onErrorChange={(error: string) => setGeoInputError(error)}
                        />
                        <div className="openhouse-input-wrapper">
                            <Form.Item
                                name="radius"
                                rules={[
                                    { required: true, message: "Distance is required" },
                                    { 
                                        validator: async (_, value) => {
                                            const num = Number(value);
                                            if (isNaN(num) || num < 1) {
                                                throw new Error('Distance must be greater than zero');
                                            }
                                        }
                                    }
                                ]}
                            >
                                <Input 
                                    className="openhouse-distance-input" 
                                    type="number" 
                                    suffix="mi" 
                                    placeholder="Distance" 
                                    min={1}
                                />
                            </Form.Item>
                        </div>
                    </div>
                    <div className="openhouse-input-wrapper">
                        <Form.Item>
                            <Button 
                                className="openhouse-search-button" 
                                type="primary" 
                                htmlType="submit" 
                                loading={loading}
                                disabled={GeoInputError !== ""}
                            >
                                Search
                            </Button>
                        </Form.Item>
                    </div>            
                </Row>
                <Row>
                    <div className="openhouse-disclaimer">*California listings only, data updated weekly.</div>
                </Row>
            </Form>

            <hr className="openhouse-divider" />

            {showDefault ? (
                <div className="openhouse-default-container">
                    <img src="/images/openhouse-default.png" alt="default-results" />
                    <div className="openhouse-default-text">Enter a city, zip code, or state to find open houses near you.</div>
                </div>
            ) 
            : (loading) ? (
                <div className="openhouse-loading-container">
                    <Skeleton active />
                    <Skeleton active />
                    <Skeleton active />
                </div>
            ) : (
                <>
                    <Row className="openhouse-filter-row">
                    <div className="openhouse-counts">{openHouseData.length} Open houses</div>
                    <div className="openhouse-filter">
                        <Select
                            placeholder="Price range"
                            className="openhouse-filter-select"
                            value={currentFilter}
                            onChange={(value: string) => setCurrentFilter(value)}
                        >
                            <Select.Option value={""}>Price range</Select.Option>
                            <Select.Option value={"less500"}>Up to $500K</Select.Option>
                            <Select.Option value={"500to1000"}>Between $500K - $1M</Select.Option>
                            <Select.Option value={"1000to3000"}>Between $1M - $3M</Select.Option>
                            <Select.Option value={"over3000"}>Over $3M</Select.Option>
                        </Select>
                    </div>
                    </Row>  
                    <Table 
                        className="openhouse-table"
                        columns={columns} 
                        dataSource={openHouseData} 
                        rowKey="address" 
                        loading={loading} 
                        pagination={pagination}
                        onChange={handleTableChange}
                        locale={{
                            emptyText: (
                                <div className="no-results-container">
                                    <img src="/images/doc-with-lens.png" alt="No results" />
                                    <div className="no-results-text">No open houses found in this area.</div>
                                    <div className="no-results-subtext">Try adjusting your search radius or explore other nearby cities.</div>
                                </div>
                            )
                        }}
                        scroll={{ y: 530, x: 1000}}
                        expandable={{
                            expandedRowRender: (record) => renderIncomeInfo(record),
                            rowExpandable: () => true,
                            expandIcon: ({expanded, onExpand, record}) => {
                                const currentRecord = openHouseData.find(item => item.address === record.address);
                                return currentRecord?.hasIncomeData ? renderExpandIcon(expanded, onExpand, record) : null;
                            },
                            expandIconColumnIndex: columns.length,
                            expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
                            onExpand: (expanded: boolean, record: OpenHouse) => {
                                if (expanded) {
                                    setExpandedRowKey(record.address);
                                    setExpandAddress({
                                        address: record.address.split(",")[0],
                                        state: com.mapAbbrFullName[record.state],
                                        city: record.city,
                                        county: (record.county || "") + " County",
                                        zip: record.zip,
                                    });
                                } else {
                                    setExpandedRowKey(null);
                                }
                            }
                        }}
                    />  
                </>
            )}
        </div>
    );
};

export default OpenHouseFinder;
